import axiosInstance from '../helpers/axiosInstance'

const requestMultipartUpload = async (client, body) => {
  const response = await axiosInstance.post(`${process.env.REACT_APP_PLATFORM_URL}${client.basePath}/new-multipart-upload`, body)
  return response.data
}

const adoptMultipartUpload = (client, responseData, key) => {
  if (!key) throw new Error('new-multipart-upload answered without a key')

  client.currentMultipartUpload.uploadId = responseData.upload_id
  client.currentMultipartUpload.fileKey = key
  client.currentMultipartUpload.presignedUploadUrls = responseData.presigned_urls
}

export default class S3Client {
  constructor({
    newMultipartUploadPath,
    generateMultipartUploadPresignedUrlsPath,
    completeMultipartUploadPath,
    listFilesWithPrefixPath,
    basePath = '/api/v1/packing-proof',
  }) {
    this.resetMultiPartUpload()
    this.newMultipartUploadPath = newMultipartUploadPath
    this.generateMultipartUploadPresignedUrlsPath = generateMultipartUploadPresignedUrlsPath
    this.completeMultipartUploadPath = completeMultipartUploadPath
    this.listFilesWithPrefixPath = listFilesWithPrefixPath
    // Endpoint group to hit. Defaults to packing-proof so existing callers stay
    // byte-identical; the return-proof client passes '/api/v1/returns-proof'.
    this.basePath = basePath
  }

  resetMultiPartUpload() {
    this.currentMultipartUpload = {
      object: null,
      uploadId: null,
      attemptedPartNumbers: new Set(),
      uploadedParts: [],
      fileKey: null,
      presignedUploadUrls: [],
    }
  }

  async createMultipartUpload(fileKey, presignedUploadUrls = 5) {
    if (!fileKey) throw new Error('createMultipartUpload needs a file key')

    const responseData = await requestMultipartUpload(this, { filename: fileKey, presign_parts: presignedUploadUrls })
    adoptMultipartUpload(this, responseData, fileKey)
    return true
  }

  // The platform mints the object key for the order and answers with it.
  async createMultipartUploadForOrder(orderId, presignedUploadUrls = 5) {
    if (orderId == null) throw new Error('createMultipartUploadForOrder needs an order id')

    const responseData = await requestMultipartUpload(this, { order_id: orderId, presign_parts: presignedUploadUrls })
    adoptMultipartUpload(this, responseData, responseData?.key)
    return responseData.key
  }

  async uploadPart(part, partIndex) {
    if (!this.currentMultipartUpload.uploadId) throw new Error('Must initialize multipart upload first')
    this.currentMultipartUpload.attemptedPartNumbers.add(partIndex + 1)

    let presignedUrl = this.currentMultipartUpload.presignedUploadUrls[partIndex + 1]

    if (!presignedUrl) {
      console.log(`No presigned URL available for part ${partIndex + 1}, generating new one...`)
      const newPresignedUrlsResponse = await this.generatePresignedUrlsForUpload([partIndex + 1])
      // update the presignedUrl
      presignedUrl = newPresignedUrlsResponse[partIndex + 1]
    }

    let response
    try {
      response = await fetch(presignedUrl, {
        method: 'PUT',
        body: part,
        headers: {
          'Content-Type': 'application/octet-stream',
        },
      })
    } catch (error) {
      // Drop the cached URL so a retry of this part number regenerates it
      // instead of re-hitting one that may have expired mid-recording.
      delete this.currentMultipartUpload.presignedUploadUrls[partIndex + 1]
      throw error
    }

    // A rejected PUT (expired presigned URL, S3 5xx) still resolves fetch; treating
    // it as uploaded used to corrupt the part list and fail the whole video at
    // complete time. Surface it here so the caller retries this part number.
    if (!response.ok) {
      delete this.currentMultipartUpload.presignedUploadUrls[partIndex + 1]
      const error = new Error(`Failed to upload part ${partIndex + 1}: HTTP ${response.status}`)
      error.httpStatus = response.status
      throw error
    }

    const eTag = response.headers.get('ETag')?.replace(/"/g, '')
    // Without an ETag the part can never be completed (S3 CORS must expose the
    // ETag header) — fail the upload of this part instead of poisoning complete.
    if (!eTag) throw new Error(`No ETag returned for part ${partIndex + 1} (is ETag exposed in the bucket CORS config?)`)

    const responseUploadPart = {
      ETag: eTag,
      PartNumber: partIndex + 1,
    }

    this.currentMultipartUpload.uploadedParts[partIndex] = responseUploadPart
    return responseUploadPart
  }

  allPartsUploaded() {
    const { attemptedPartNumbers, uploadedParts } = this.currentMultipartUpload
    if (!attemptedPartNumbers.size) return false
    return [...attemptedPartNumbers].every(partNumber => uploadedParts[partNumber - 1])
  }

  async completeMultipartUpload() {
    if (this.currentMultipartUpload.attemptedPartNumbers.size === 0) throw new Error('No parts to complete')
    if (!this.currentMultipartUpload.uploadId) throw new Error('No multipart update to complete')
    if (!this.allPartsUploaded()) throw new Error('Not all parts have been uploaded')

    const completedParts = this.currentMultipartUpload.uploadedParts.map((part, index) => ({
      etag: part.ETag,
      part_number: index + 1,
    }))

    // throw error if any uploaded parts have no etag
    if (completedParts.some(part => !part.etag)) throw new Error('Not all uploaded parts have an ETag')

    console.log('Completing multipart upload for', this.currentMultipartUpload.fileKey)

    const body = {
      upload_id: this.currentMultipartUpload.uploadId,
      key: this.currentMultipartUpload.fileKey,
      parts: completedParts,
    }

    const response = await axiosInstance.post(`${process.env.REACT_APP_PLATFORM_URL}${this.basePath}/complete-upload`, body)

    this.resetMultiPartUpload()
    console.log('Multipart upload completed successfully', response)
    return response
  }

  async listFilesWithPrefix(prefix) {
    const params = {
      prefix: prefix,
    }
    const response = await axiosInstance.get(`${process.env.REACT_APP_PLATFORM_URL}${this.basePath}/list`, { params })
    // console.log('List files with prefix response:', response.data)
    const objects = []
    if (response.data) {
      response.data.forEach(obj => {
        objects.push(obj)
      })
    }
    return objects
  }

  async generatePresignedUrlsForUpload(partNumbers) {
    if (!this.currentMultipartUpload.uploadId) throw new Error('Must initialize multipart upload first')
    if (!partNumbers || !Array.isArray(partNumbers) || partNumbers.length === 0) {
      throw new Error('Must provide an array of part numbers to generate presigned URLs')
    }
    const body = {
      upload_id: this.currentMultipartUpload.uploadId,
      part_numbers: partNumbers,
      key: this.currentMultipartUpload.fileKey,
    }

    const response = await axiosInstance.post(`${process.env.REACT_APP_PLATFORM_URL}${this.basePath}/upload-presigned-urls`, body)

    return response.data?.presigned_urls
  }
}
